import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Printer, Search } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { pickCatalogName } from '@/i18n/localizeItem';

const API_URL = process.env.REACT_APP_API_URL;
const LOCALES = { tr: 'tr-TR', en: 'en-US', de: 'de-DE', fr: 'fr-FR', es: 'es-ES' };
export const QR_PREFIX = 'PAYDAAI:';

const escapeHtml = (s) => String(s || '').replace(/[&<>"']/g, (c) => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
}[c]));

/**
 * Printable A4 sheet of QR labels (3 × 8, 70 × 37 mm — the common
 * "24 labels per A4" sticker sheet). Each QR holds PAYDAAI:<item id>, which
 * the stock scanner recognises, so products without a manufacturer barcode
 * (cable, connectors, boxes…) can be scanned too.
 */
export function buildLabelsHtml(labels, title) {
  const cells = labels.map((l) => `
    <div class="label">
      <img src="${l.qr}" alt="" />
      <div class="text">
        <div class="name">${escapeHtml(l.name)}</div>
        ${l.meta ? `<div class="meta">${escapeHtml(l.meta)}</div>` : ''}
      </div>
    </div>`).join('');
  return `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title>
<style>
  @page { size: A4; margin: 10mm 0 0 0; }
  * { box-sizing: border-box; }
  body { margin: 0; font-family: Arial, Helvetica, sans-serif; }
  .sheet { display: grid; grid-template-columns: repeat(3, 70mm); grid-auto-rows: 37mm; width: 210mm; }
  .label { display: flex; align-items: center; gap: 2.5mm; padding: 2.5mm 3mm; overflow: hidden; break-inside: avoid; }
  .label img { width: 28mm; height: 28mm; flex: none; }
  .text { min-width: 0; }
  .name { font-size: 9.5pt; font-weight: bold; line-height: 1.2; max-height: 3.6em; overflow: hidden; }
  .meta { font-size: 7.5pt; color: #444; margin-top: 1mm; line-height: 1.2; max-height: 2.4em; overflow: hidden; }
  @media screen { body { background: #eee; } .sheet { background: #fff; margin: 10px auto; } .label { outline: 1px dashed #ccc; } }
</style></head><body><div class="sheet">${cells}</div>
<script>window.onload = function () { setTimeout(function () { window.print(); }, 300); };</script>
</body></html>`;
}

export function StockLabelsDialog({ open, onClose }) {
  const { t, i18n } = useTranslation('quotes');
  const lang = (i18n.language || 'tr').split('-')[0];
  const locale = LOCALES[lang] || 'tr-TR';
  const [rows, setRows] = useState(null);
  const [selected, setSelected] = useState({});
  const [copies, setCopies] = useState('1');
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected({});
    setSearch('');
    setCopies('1');
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/quote/stock`, { params: { scope: 'all', page_size: 1000 } });
        setRows(data.items || []);
      } catch {
        setRows([]);
        toast.error(t('common.error'));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const visible = useMemo(() => {
    const q = search.trim().toLocaleLowerCase(locale);
    const list = rows || [];
    if (!q) return list;
    return list.filter((r) => [r.name, r.name_en, r.sku, r.brand, r.model, r.category]
      .some((v) => (v || '').toLocaleLowerCase(locale).includes(q)));
  }, [rows, search, locale]);

  const count = Object.values(selected).filter(Boolean).length;
  const allVisibleSelected = visible.length > 0 && visible.every((r) => selected[r.id]);

  const toggleAll = () => {
    setSelected((prev) => {
      const next = { ...prev };
      visible.forEach((r) => { next[r.id] = !allVisibleSelected; });
      return next;
    });
  };

  const print = async () => {
    const n = Math.min(Math.max(parseInt(copies, 10) || 1, 1), 50);
    const chosen = (rows || []).filter((r) => selected[r.id]);
    if (!chosen.length) return;
    // Open the window inside the click so pop-up blockers allow it.
    const win = window.open('', '_blank');
    if (!win) {
      toast.error(t('stock.labels.popupBlocked'));
      return;
    }
    setBusy(true);
    try {
      const QRCode = (await import('qrcode')).default;
      const labels = [];
      for (const r of chosen) {
        // eslint-disable-next-line no-await-in-loop
        const qr = await QRCode.toDataURL(`${QR_PREFIX}${r.id}`, { margin: 0, width: 240, errorCorrectionLevel: 'M' });
        const meta = [r.brand, r.model, r.sku].filter(Boolean).join(' · ');
        for (let i = 0; i < n; i += 1) labels.push({ qr, name: pickCatalogName(r, lang), meta });
      }
      win.document.open();
      win.document.write(buildLabelsHtml(labels, t('stock.labels.title')));
      win.document.close();
      onClose();
    } catch {
      win.close();
      toast.error(t('common.error'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('stock.labels.title')}</DialogTitle>
          <DialogDescription>{t('stock.labels.help')}</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#94A3B8]" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('stock.searchPlaceholder')} className="pl-9" />
          </div>
          <Button variant="outline" onClick={toggleAll} disabled={!visible.length}>
            {allVisibleSelected ? t('stock.labels.clear') : t('stock.labels.selectAll')}
          </Button>
        </div>
        <ul className="max-h-[45vh] divide-y divide-[#F1F5F9] overflow-y-auto rounded-xl border border-[#E5E5E5]">
          {rows === null ? (
            <li className="py-6 text-center text-sm text-[#595959]">{t('common.loading')}</li>
          ) : visible.length === 0 ? (
            <li className="py-6 text-center text-sm text-[#94A3B8]">{t('stock.emptyFiltered')}</li>
          ) : visible.map((r) => (
            <li key={r.id}>
              <label className="flex cursor-pointer items-center gap-3 px-3 py-2.5 hover:bg-slate-50">
                <Checkbox
                  checked={!!selected[r.id]}
                  onCheckedChange={(v) => setSelected((prev) => ({ ...prev, [r.id]: !!v }))}
                  data-testid={`label-check-${r.id}`}
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-[#1A1A2E]">{pickCatalogName(r, lang)}</span>
                  <span className="block truncate text-xs text-[#94A3B8]">{[r.brand, r.model, r.sku].filter(Boolean).join(' · ')}</span>
                </span>
              </label>
            </li>
          ))}
        </ul>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-[#475569]">{t('stock.labels.copies')}</span>
          <Input inputMode="numeric" value={copies} onChange={(e) => setCopies(e.target.value)} className="w-20" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
          <Button onClick={print} disabled={!count || busy} className="bg-[#2563EB] hover:bg-[#1E4ED8]" data-testid="labels-print-btn">
            <Printer className="mr-2 h-4 w-4" /> {t('stock.labels.print', { count })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
